import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface MessageContact {
  id: string;
  nom: string;
  prenom?: string;
  email: string;
  telephone?: string;
  sujet: string;
  message: string;
  date_creation: string;
  lu: boolean;
  repondu: boolean;
}

export default function MessagesContactPage() {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const { user, role } = useAuth();
  const [messages, setMessages] = useState<MessageContact[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MessageContact | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }
    loadMessages();
  }, [role, navigate]);

  useEffect(() => {
    if (messageId && messages.length > 0) {
      const message = messages.find(m => m.id === messageId);
      if (message) {
        setSelectedMessage(message);
        markAsRead(message.id);
      }
    }
  }, [messageId, messages]);

  const loadMessages = async () => {
    const { data, error } = await supabase
      .from('messages_contact')
      .select('*')
      .order('date_creation', { ascending: false });

    if (!error && data) {
      setMessages(data);
      
      // Si un messageId est dans l'URL, sélectionner ce message
      if (messageId) {
        const message = data.find(m => m.id === messageId);
        if (message) {
          setSelectedMessage(message);
          markAsRead(message.id);
        }
      }
    }
    setLoading(false);
  };

  const markAsRead = async (id: string) => {
    await supabase
      .from('messages_contact')
      .update({ lu: true })
      .eq('id', id);

    setMessages(prev =>
      prev.map(m => (m.id === id ? { ...m, lu: true } : m))
    );
  };

  const markAsReplied = async (id: string) => {
    await supabase
      .from('messages_contact')
      .update({ repondu: true })
      .eq('id', id);

    setMessages(prev =>
      prev.map(m => (m.id === id ? { ...m, repondu: true } : m))
    );
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    setSending(true);
    
    // Marquer comme répondu
    await markAsReplied(selectedMessage.id);
    
    // Créer un mailto link pour ouvrir le client email
    const subject = encodeURIComponent(`Re: ${selectedMessage.sujet}`);
    const body = encodeURIComponent(replyText);
    window.location.href = `mailto:${selectedMessage.email}?subject=${subject}&body=${body}`;
    
    setReplyText('');
    setSending(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (message: MessageContact) => {
    if (message.repondu) {
      return <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">Répondu</span>;
    }
    if (message.lu) {
      return <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap">Lu</span>;
    }
    return <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full whitespace-nowrap">Nouveau</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <div className="bg-white shadow-md border-b-4 border-clinic-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-2xl text-gray-600"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-clinic-blue-dark flex items-center gap-3">
                  <i className="ri-mail-line"></i>
                  Messages de Contact
                </h1>
                <p className="text-gray-600 mt-1">Gérez les messages reçus via le formulaire de contact</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-4 py-2 bg-clinic-blue text-white rounded-lg font-semibold whitespace-nowrap">
                {messages.filter(m => !m.lu).length} nouveaux
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des messages */}
          <div className="lg:col-span-1 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-clinic-blue to-clinic-green">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <i className="ri-inbox-line"></i>
                Boîte de réception
              </h2>
            </div>
            <div className="divide-y divide-gray-100 max-h-[calc(100vh-300px)] overflow-y-auto">
              {messages.length === 0 ? (
                <div className="p-8 text-center">
                  <i className="ri-mail-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Aucun message</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => {
                      setSelectedMessage(message);
                      markAsRead(message.id);
                      navigate(`/admin/messages-contact/${message.id}`);
                    }}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedMessage?.id === message.id ? 'bg-blue-50 border-l-4 border-clinic-blue' : ''
                    } ${!message.lu ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className={`font-semibold text-sm ${!message.lu ? 'text-clinic-blue-dark' : 'text-gray-700'}`}>
                        {message.prenom ? `${message.prenom} ${message.nom}` : message.nom}
                      </h3>
                      {!message.lu && (
                        <span className="w-2 h-2 bg-clinic-blue rounded-full flex-shrink-0 mt-1"></span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 font-medium mb-1">{message.sujet}</p>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">{message.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {formatDate(message.date_creation)}
                      </span>
                      {getStatusBadge(message)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Détails du message */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden">
            {selectedMessage ? (
              <div className="flex flex-col h-[calc(100vh-200px)]">
                {/* En-tête du message */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-clinic-blue/5 to-clinic-green/5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-clinic-blue-dark mb-2">{selectedMessage.sujet}</h2>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-user-line"></i>
                        <span className="font-semibold">
                          {selectedMessage.prenom ? `${selectedMessage.prenom} ${selectedMessage.nom}` : selectedMessage.nom}
                        </span>
                        <span>•</span>
                        <span>{formatDate(selectedMessage.date_creation)}</span>
                      </div>
                    </div>
                    {getStatusBadge(selectedMessage)}
                  </div>

                  {/* Informations de contact */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-clinic-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-clinic-blue text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <a
                          href={`mailto:${selectedMessage.email}`}
                          className="text-sm font-semibold text-clinic-blue hover:underline cursor-pointer"
                        >
                          {selectedMessage.email}
                        </a>
                      </div>
                    </div>
                    {selectedMessage.telephone && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-clinic-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-phone-line text-clinic-green text-lg"></i>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Téléphone</p>
                          <a
                            href={`tel:${selectedMessage.telephone}`}
                            className="text-sm font-semibold text-clinic-green hover:underline cursor-pointer"
                          >
                            {selectedMessage.telephone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contenu du message */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p>
                  </div>
                </div>

                {/* Zone de réponse */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-bold text-clinic-blue-dark mb-4 flex items-center gap-2">
                    <i className="ri-reply-line"></i>
                    Répondre
                  </h3>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Écrivez votre réponse ici..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-clinic-blue resize-none"
                    rows={4}
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-gray-500">
                      {replyText.length}/500 caractères
                    </span>
                    <button
                      onClick={handleReply}
                      disabled={!replyText.trim() || sending}
                      className="px-6 py-3 bg-gradient-to-r from-clinic-blue to-clinic-green text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      {sending ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill"></i>
                          Envoyer par email
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <i className="ri-information-line"></i> Cliquer sur "Envoyer par email" ouvrira votre client email avec le message pré-rempli
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-[calc(100vh-200px)] flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-mail-open-line text-8xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500 text-lg">Sélectionnez un message pour le lire</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
